import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  toRaw,
  nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { clearDefaultValues } from "@/common/clear-default-values.js";
import cloneDeepWith from "lodash/cloneDeepWith";
import cloneDeep from "lodash/cloneDeep";
import { useI18n } from "vue-i18n";

export function useRestTable(props = {}) {
  const route = useRoute();
  const router = useRouter();

  let abortController = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const items = ref([]);
  const meta = reactive({
    total_count: 0,
  });

  const getDefaultFilters = () => {
    const baseFilters = {
      search: "",
      sort_by: null,
      sort_order: "asc",
      ...props.initialFilters,
    };
    const paginationFilters = {
      page: 1,
      per_page: props.initialItemsPerPage ?? 10,
    };

    const filters = {
      ...baseFilters,
      ...(props.isPaginationDisabled ? {} : paginationFilters),
    };
    return filters;
  };

  const filters = reactive(getDefaultFilters());

  const { t } = useI18n();

  // COMPUTED
  const isLoading = computed(() => loading.value);
  const totalItems = computed(() => meta.total_count);
  const errorDetailsMessage = computed(() => {
    const err = error.value;
    return (
      err?.response?.data?.message ??
      err?.message ??
      t("errors.something_went_wrong")
    );
  });

  // METHODS
  const fetchData = () => {
    if (abortController.value) {
      abortController.value.abort();
    }
    abortController.value = new AbortController();
    error.value = null;
    loading.value = true;

    const plainFilters = cloneDeep(toRaw(filters));

    props
      .fetchHandler?.(plainFilters, { signal: abortController.value.signal })
      .then((result) => {
        items.value = result?.items ?? [];
        meta.total_count = result?.meta?.total_count ?? 0;
        if (!items.value.length && filters.page > 1) {
          updateFilters({ page: 1 });
        }
        loading.value = false;
      })
      .catch((err) => {
        if (err.message !== "canceled") {
          error.value = err;
          loading.value = false;
        }
      });
  };

  const updateFilters = async (newFilters) => {
    if (!props.disablePagination) {
      filters.page = 1;
    }

    const processedFilters = cloneDeepWith(newFilters, (value) => {
      // replacing Date with ISO string
      if (value instanceof Date) {
        return value.toISOString();
      }
      // no changes
      return undefined;
    });

    Object.assign(filters, processedFilters);
    await nextTick();
    await updateRouterTableState();
  };

  const onSort = (sortOptions) => {
    updateFilters({
      sort_by: sortOptions.sortOrder === null ? null : sortOptions.sortBy,
      sort_order: sortOptions.sortOrder || "asc", // asc is the default value
      page: props.disablePagination ? undefined : 1,
    });
  };

  // ROUTER STATE SYNCHRONIZATION
  const getTableFilters = (namespace = props.namespace) => {
    const tableQuery = route.query[`table_${namespace}`];
    let parsedFilters = {};
    if (tableQuery && typeof tableQuery === "string") {
      try {
        parsedFilters = JSON.parse(tableQuery);
      } catch (e) {
        console.error("Failed to parse table state from URL query.", e);
        removeRouterTableState();
      }
    }
    return parsedFilters;
  };

  const fetchRouterTableState = () => {
    const parsedFilters = getTableFilters(props.namespace);

    Object.assign(filters, getDefaultFilters(), parsedFilters);
  };

  const updateRouterTableState = async () => {
    // Create a deep clone to prevent mutating the original `filters` object
    const routeQueryFilters = cloneDeep(toRaw(filters));

    const defaultFilters = getDefaultFilters();
    const tableQueryFilters =
      clearDefaultValues(routeQueryFilters, defaultFilters) ?? {};

    const newParams = { ...toRaw(route.params) };

    const location = {
      name: route.name,
      params: newParams,
      query: {
        ...route.query,
        [`table_${props.namespace}`]: Object.keys(tableQueryFilters).length
          ? JSON.stringify(tableQueryFilters)
          : undefined,
      },
    };

    await router.push(location);
  };

  const removeRouterTableState = () => {
    const query = { ...route.query };
    delete query[`table_${props.namespace}`];
    router.replace({ query });
  };

  // LIFECYCLE & WATCHERS
  watch(
    [
      () => route.path, // Watch the base path
      () => route.query[`table_${props.namespace}`], // Watch ONLY this table's param
    ],
    () => {
      // This callback ONLY runs if path or this specific table state changes.
      fetchRouterTableState();
      fetchData();
    },
    { flush: "post" }, // wait for DOM updates
  );

  onMounted(() => {
    fetchRouterTableState();
    fetchData();
  });

  onBeforeUnmount(() => {
    if (abortController.value) {
      abortController.value.abort();
    }
  });

  // EXPORT
  return {
    loading,
    isLoading,
    error,
    errorDetailsMessage,
    items,
    meta,
    totalItems,
    filters,
    fetchData,
    updateFilters,
    onSort,
    getTableFilters,
  };
}
